// src/lib/percorsi.js
import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  getDocs,
  query,
  runTransaction,
  where,
} from 'firebase/firestore';
import { appendTicketMessage } from './technicalTickets';
import { db } from '@/firebaseConfig';
import { shouldSuppressNotifications } from './operationalHold';

/* -------- utils date -------- */
const HIDE_AFTER_DAYS = 45;

function addDaysISO(iso, days) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d)) return null;
  d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString();
}

// somma mesi mantenendo il comportamento standard JS sui giorni “non esistenti”
function addMonthsKeepDayISO(iso, months) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d)) return null;
  d.setMonth(d.getMonth() + Number(months || 0));
  return d.toISOString();
}

function toDateSafe(value) {
  if (value === undefined || value === null) return null;
  if (typeof value?.toDate === 'function') {
    try {
      const converted = value.toDate();
      return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
    } catch {
      return null;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d?.getTime?.()) ? null : d;
}

function computeDurationExpireAt(data = {}) {
  const dur = Number(data?.duration_months);
  if (!Number.isFinite(dur) || dur <= 0) return null;
  const anchorDate = toDateSafe(data?.plannedStartAt || data?.createdAt);
  if (!anchorDate) return null;
  return addMonthsKeepDayISO(anchorDate.toISOString(), dur);
}

function addWorkingDaysISO(iso, workingDays) {
  const base = toDateSafe(iso);
  if (!base) return null;
  let remaining = Number(workingDays) || 0;
  if (remaining === 0) return base.toISOString();
  const dir = remaining > 0 ? 1 : -1;
  const d = new Date(base);
  while (remaining !== 0) {
    d.setDate(d.getDate() + dir);
    const day = d.getDay(); // 0 domenica, 6 sabato
    if (day !== 0 && day !== 6) {
      remaining -= dir;
    }
  }
  return d.toISOString();
}

function normalizeIso(value) {
  const date = toDateSafe(value);
  return date ? date.toISOString() : null;
}

function toPositiveInt(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
}

function getConsegnaStepIndex(steps = []) {
  if (!Array.isArray(steps) || !steps.length) return -1;
  return steps.findIndex((step) => stepKeyFromName(step?.name) === 'consegna');
}

function computeAutomaticExpireAt(data = {}) {
  if (data?.consulenzeOnly) return null;
  const steps = Array.isArray(data.steps) ? data.steps : [];
  const consegnaIndex = getConsegnaStepIndex(steps);
  if (consegnaIndex < 0) return null;

  const consegnaStep = steps[consegnaIndex] || {};
  const anchorDate = toDateSafe(consegnaStep?.doneAt || consegnaStep?.plannedAt);
  if (!anchorDate) return null;

  return addDaysISO(anchorDate.toISOString(), 30);
}

function computePercorsoExpireAt(data = {}) {
  if (!data?.consulenzeOnly) {
    return computeAutomaticExpireAt(data) || computeDurationExpireAt(data);
  }
  return computeDurationExpireAt(data);
}

function resolveExpireAt(data = {}) {
  const computed = computePercorsoExpireAt(data);
  if (computed) return computed;
  const stored = toDateSafe(data?.expireAt);
  return stored ? stored.toISOString() : null;
}

function buildConsegnaDerivedFields(data = {}) {
  const steps = Array.isArray(data.steps) ? data.steps : [];
  const consegnaIndex = getConsegnaStepIndex(steps);
  if (consegnaIndex < 0) {
    return {
      deliveryDoneAt: null,
      hideAfter45At: null,
    };
  }

  const deliveryDoneAt = toDateSafe(steps[consegnaIndex]?.doneAt);
  if (!deliveryDoneAt) {
    return {
      deliveryDoneAt: null,
      hideAfter45At: null,
    };
  }

  return {
    deliveryDoneAt,
    hideAfter45At: new Date(deliveryDoneAt.getTime() + HIDE_AFTER_DAYS * 24 * 60 * 60 * 1000),
  };
}

function resolveProgCustomState(step = {}) {
  const progCustomizzato = !!step?.progCustomizzato;
  return {
    progCustomizzato,
    progCustomizzatoAt: progCustomizzato ? normalizeIso(step?.progCustomizzatoAt) : null,
  };
}

function patchConsegnaMirrorEntries(entries, percorsoId, done, doneAtIso) {
  if (!Array.isArray(entries) || !percorsoId) {
    return { changed: false, nextEntries: entries };
  }

  let changed = false;
  const normalizedDone = !!done;
  const normalizedDoneAt = normalizedDone
    ? (normalizeIso(doneAtIso) || new Date().toISOString())
    : null;

  const nextEntries = entries.map((entry) => {
    if (!entry || typeof entry !== 'object') return entry;
    if (entry.percorsoId !== percorsoId) return entry;

    const currentDone = !!entry.consegnaConfirmed;
    const currentDoneAt = normalizeIso(entry.consegnaConfirmedAt);

    if (currentDone === normalizedDone && currentDoneAt === normalizedDoneAt) {
      return entry;
    }

    changed = true;
    return {
      ...entry,
      consegnaConfirmed: normalizedDone,
      consegnaConfirmedAt: normalizedDoneAt,
    };
  });

  return { changed, nextEntries };
}

function patchProgCustomMirrorEntries(entries, updatesByPercorsoId) {
  if (!Array.isArray(entries) || !updatesByPercorsoId?.size) {
    return { changed: false, nextEntries: entries };
  }

  let changed = false;
  const opNowIso = new Date().toISOString();

  const nextEntries = entries.map((entry) => {
    if (!entry || typeof entry !== 'object') return entry;
    const percorsoId = entry.percorsoId;
    if (!percorsoId) return entry;

    const nextState = updatesByPercorsoId.get(percorsoId);
    if (!nextState) return entry;

    const nextCustom = !!nextState.progCustomizzato;
    const nextCustomAt = nextCustom
      ? (normalizeIso(nextState.progCustomizzatoAt) || normalizeIso(entry.progCustomizzatoAt) || opNowIso)
      : null;
    const currentCustom = !!entry.progCustomizzato;
    const currentCustomAt = normalizeIso(entry.progCustomizzatoAt);

    if (currentCustom === nextCustom && currentCustomAt === nextCustomAt) {
      return entry;
    }

    changed = true;
    return {
      ...entry,
      progCustomizzato: nextCustom,
      progCustomizzatoAt: nextCustomAt,
    };
  });

  return { changed, nextEntries };
}

async function syncConsegnaMirrorForInvii({ userUid, percorsoId, done, doneAtIso }) {
  if (!userUid || !percorsoId) return;

  const inviiSnap = await getDocs(
    query(collection(db, 'inviidigiornata'), where('uid', '==', userUid))
  );
  const refs = [
    doc(db, 'alimentazioni', userUid),
    ...inviiSnap.docs.map((docSnap) => docSnap.ref),
  ];
  const stableDoneAtIso = done
    ? (normalizeIso(doneAtIso) || new Date().toISOString())
    : null;

  await Promise.all(refs.map((ref) => runTransaction(db, async (transaction) => {
    const currentSnap = await transaction.get(ref);
    if (!currentSnap.exists()) return;

    const rawEntries = currentSnap.data()?.alimentazioni;
    const { changed, nextEntries } = patchConsegnaMirrorEntries(
      Array.isArray(rawEntries) ? rawEntries : [],
      percorsoId,
      done,
      stableDoneAtIso
    );
    if (changed) transaction.update(ref, { alimentazioni: nextEntries });
  })));
}

async function syncProgCustomMirrorForInvii({ userUid, updatesByPercorsoId }) {
  if (!userUid || !updatesByPercorsoId?.size) return;

  const inviiSnap = await getDocs(
    query(collection(db, 'inviidigiornata'), where('uid', '==', userUid))
  );
  const refs = [
    doc(db, 'alimentazioni', userUid),
    ...inviiSnap.docs.map((docSnap) => docSnap.ref),
  ];

  await Promise.all(refs.map((ref) => runTransaction(db, async (transaction) => {
    const currentSnap = await transaction.get(ref);
    if (!currentSnap.exists()) return;

    const rawEntries = currentSnap.data()?.alimentazioni;
    const { changed, nextEntries } = patchProgCustomMirrorEntries(
      Array.isArray(rawEntries) ? rawEntries : [],
      updatesByPercorsoId
    );
    if (changed) transaction.update(ref, { alimentazioni: nextEntries });
  })));
}

/* Normalizza il nome dello step in chiave stabile */
export function stepKeyFromName(name = '') {
  const n = (name || '').toLowerCase();
  if (n.includes('questionario')) return 'questionario';
  if (n.includes('consegna'))     return 'consegna';
  if (n.includes('consulenza'))   return 'consulenza';
  if (n.includes('feedback'))     return 'feedback_programmi';
  if (n.includes('check') || n.includes('scadenza')) return 'check_finale';
  return null;
}

export function normalizeConsulenzaStatus(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'contattata' || raw === 'contattato') return 'Contattata';
  if (raw === 'prenotata' || raw === 'prenotato') return 'Prenotata';
  if (raw === 'rifiutata' || raw === 'rifiutato') return 'Rifiutata';
  if (raw === 'fatta' || raw === 'fatto') return 'Fatta';
  if (raw === 'scalata' || raw === 'scalato') return 'Scalata';
  return 'Da fare';
}

export function normalizeConsulenzaType(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw.includes('nutriz')) return 'nutrizionale';
  if (raw.includes('tecnic')) return 'tecnica';
  return '';
}

export function isConsulenzaDoneStatus(value) {
  const normalized = normalizeConsulenzaStatus(value);
  return normalized === 'Fatta' || normalized === 'Rifiutata' || normalized === 'Scalata';
}

export function isPercorsoStepEffectivelyDone(step = {}) {
  if (!step || typeof step !== 'object') return false;
  if (stepKeyFromName(step?.name) === 'consulenza') {
    return isConsulenzaDoneStatus(step?.consulenzaStatus);
  }
  return !!step.done;
}

export function isRealCheckFinaleStep(step = {}) {
  return stepKeyFromName(step?.name) === 'check_finale' && !step?.convertedFromQuestionario;
}

export function getRealCheckFinaleDoneEntry(steps = []) {
  let match = null;
  (Array.isArray(steps) ? steps : []).forEach((step, index) => {
    if (isRealCheckFinaleStep(step) && isPercorsoStepEffectivelyDone(step)) {
      match = { step, index };
    }
  });
  return match;
}

export function isTechnicalConsulenzaStep(step = {}) {
  if (stepKeyFromName(step?.name) !== 'consulenza') return false;
  const normalizedType = normalizeConsulenzaType(step?.consulenzaType);
  return normalizedType === 'tecnica' || normalizedType === '';
}

export function isAutoScalabileTechnicalConsulenzaStep(step = {}) {
  if (!isTechnicalConsulenzaStep(step)) return false;
  return normalizeConsulenzaStatus(step?.consulenzaStatus) === 'Da fare';
}

export function applyAutomaticTechnicalConsulenzeScalata(steps = [], options = {}) {
  const sourceSteps = Array.isArray(steps) ? steps : [];
  const nextSteps = sourceSteps.map((step) => (
    step && typeof step === 'object' ? { ...step } : step
  ));
  const checkEntry = getRealCheckFinaleDoneEntry(nextSteps);
  if (!checkEntry) {
    return { steps: nextSteps, changed: false, checkEntry: null, changes: [] };
  }

  const allowedStepIndices = Array.isArray(options.allowedStepIndices)
    ? new Set(
        options.allowedStepIndices
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value >= 0)
      )
    : null;
  const fallbackNowIso = new Date().toISOString();
  const nowIso = normalizeIso(options.nowIso) || fallbackNowIso;
  const changes = [];

  nextSteps.forEach((step, stepIndex) => {
    if (!step || typeof step !== 'object') return;
    if (allowedStepIndices && !allowedStepIndices.has(stepIndex)) return;
    if (!isAutoScalabileTechnicalConsulenzaStep(step)) return;

    const nextScalataAt = normalizeIso(step?.scalataAt) || nowIso;
    changes.push({
      stepIndex,
      stepName: step?.name || null,
      rawConsulenzaType: step?.consulenzaType ?? null,
      previousStatus: normalizeConsulenzaStatus(step?.consulenzaStatus),
      previousDone: !!step?.done,
      previousDoneAt: step?.doneAt || null,
      previousScalataAt: step?.scalataAt || null,
      nextStatus: 'Scalata',
      nextDone: true,
      nextDoneAt: null,
      nextScalataAt,
      checkIndex: checkEntry.index,
      checkName: checkEntry.step?.name || null,
      checkDoneAt: checkEntry.step?.doneAt || null,
    });

    nextSteps[stepIndex] = {
      ...step,
      consulenzaStatus: 'Scalata',
      done: true,
      doneAt: null,
      scalataAt: nextScalataAt,
    };
  });

  return {
    steps: nextSteps,
    changed: changes.length > 0,
    checkEntry,
    changes,
  };
}

const TOP_PRODUCT_SKUS = new Set([
  'fc4top',
  'fc6top',
  'fc10top',
  '10topsoloallenamento',
  'prot1top',
  'protocollo1top',
  'prot2top',
  'protocollo2top',
  'prot3top',
  'protocollo3top',
]);

const normalizeTopText = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).toLowerCase().trim();
};

const normalizeTopSku = (value) => normalizeTopText(value).replace(/[^a-z0-9]/g, '');

const SOLO_ALLENAMENTO_IDENTIFIERS = Object.freeze([
  '1 Solo Allenamento',
  '1soloallenamento',
  '4 Solo Allenamento',
  '4soloallenamento',
  '4+4 Solo Allenamento',
  '4+4soloallenamento',
  '6 Solo Allenamento',
  '6soloallenamento',
  '6+6 Solo Allenamento',
  '6+6soloallenamento',
  '10 Top Solo Allenamento',
  '10topsoloallenamento',
]);

const SOLO_ALIMENTAZIONE_IDENTIFIERS = Object.freeze([
  '1 Solo Dieta',
  '1solodieta',
  '4 Solo Dieta',
  '4solodieta',
  '4+4 Solo Dieta',
  '4+4solodieta',
  '6 Solo Dieta',
  '6+6solodieta',
]);

const SOLO_ALLENAMENTO_SKUS = new Set(
  SOLO_ALLENAMENTO_IDENTIFIERS.map(normalizeTopSku).filter(Boolean)
);

const SOLO_ALIMENTAZIONE_SKUS = new Set(
  SOLO_ALIMENTAZIONE_IDENTIFIERS.map(normalizeTopSku).filter(Boolean)
);

const hasTopSkuMatch = (sku) => {
  if (!sku) return false;
  if (TOP_PRODUCT_SKUS.has(sku)) return true;
  for (const topSku of TOP_PRODUCT_SKUS) {
    if (sku.includes(topSku)) return true;
  }
  return false;
};

const isTopProductValue = (value) => {
  const raw = normalizeTopText(value);
  if (!raw) return false;
  const sku = normalizeTopSku(raw);
  return hasTopSkuMatch(sku);
};

const isTopProduct = (data = {}) => {
  const candidates = [
    data.productSku,
    data.productId,
    data.productName,
    data.title,
  ];
  return candidates.some(isTopProductValue);
};

const hasSkuSetMatch = (value, skuSet) => {
  const sku = normalizeTopSku(value);
  if (!sku) return false;
  if (skuSet.has(sku)) return true;
  for (const match of skuSet) {
    if (sku.includes(match)) return true;
  }
  return false;
};

const resolveProgramDeliveryMode = (data = {}) => {
  const candidates = [
    data.productSku,
    data.productId,
    data.productName,
    data.title,
  ];
  const isSoloAllenamento = candidates.some((value) =>
    hasSkuSetMatch(value, SOLO_ALLENAMENTO_SKUS)
  );
  const isSoloAlimentazione = candidates.some((value) =>
    hasSkuSetMatch(value, SOLO_ALIMENTAZIONE_SKUS)
  );
  return { isSoloAllenamento, isSoloAlimentazione };
};

/* -------- helpers consulenze -------- */
function _toInt(val) {
  const n = Number(val);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

// crea N step consulenza di un certo tipo a partire da un'ancora + offset giorni
function _buildConsulenze(type, count, anchorISO, options = {}) {
  const offset = Number.isFinite(Number(options.offsetDays)) ? Number(options.offsetDays) : 1;
  const placement = options.placement || 'after-consegna';
  const plannedBase = anchorISO || new Date().toISOString();
  const planned = addDaysISO(plannedBase, offset);
  const nowIso = new Date().toISOString();
  const out = [];
  for (let i = 0; i < count; i++) {
    out.push({
      name: 'consulenza',
      plannedAt: planned,
      done: false,
      doneAt: null,
      consulenzaStatus: 'Da fare',
      consulenzaType: type,       // 'Tecnica' | 'Nutrizionale'
      TrainerNome: '',
      createdAt: nowIso,
      consulenzaPlacement: placement,
      consulenzaOffsetDays: offset,
    });
  }
  return out;
}

function _countConsulenzeByType(steps = []) {
  let tecnica = 0, nutrizionale = 0;
  for (const s of steps) {
    if (stepKeyFromName(s?.name) === 'consulenza') {
      const ty = (s?.consulenzaType || '').toLowerCase();
      if (ty === 'tecnica') tecnica++;
      else if (ty === 'nutrizionale') nutrizionale++;
      else tecnica++; // fallback se mancava il tipo
    }
  }
  return { tecnica, nutrizionale };
}

function _generateSequenceGroupId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}

async function _createConsulenzeOnlyPercorso({
  uid,
  userEmail,
  productId,
  productName,
  orderRef,
  orderSource,
  nowIso,
  plannedStartAt,
  titleBase,
  durationMonths,
  consTech,
  consNutr,
}) {
  const sequenceGroupId = _generateSequenceGroupId();
  const createdAtIso = toDateSafe(nowIso)?.toISOString() || new Date().toISOString();
  const baseStartIso = toDateSafe(plannedStartAt)?.toISOString() || createdAtIso;
  const plannedStartValue = baseStartIso;

  const steps = [];
  if (consNutr > 0) {
    steps.push(..._buildConsulenze('Nutrizionale', consNutr, plannedStartValue, {
      offsetDays: 0,
      placement: 'standalone',
    }));
  }

  const expireAt = resolveExpireAt({
    consulenzeOnly: true,
    plannedStartAt: plannedStartValue,
    createdAt: createdAtIso,
    duration_months: durationMonths,
    steps,
  });
  if (consTech > 0) {
    steps.push(..._buildConsulenze('Tecnica', consTech, plannedStartValue, {
      offsetDays: 0,
      placement: 'standalone',
    }));
  }

  const ref = await addDoc(collection(db, 'percorsi'), {
    uid: uid || null,
    userEmail,
    title: (titleBase || productName || 'Prodotto') + ' - Consulenze',
    productId,
    productName,
    sequenceGroupId,
    sequenceIndex: 1,
    sequenceTotal: 1,
    createdAt: createdAtIso,
    plannedStartAt: plannedStartValue,
    duration_months: durationMonths,
    expireAt,
    orderRef: orderRef ? String(orderRef) : null,
    orderSource: orderSource || null,
    postponeDays: 0,
    historyLocked: false,
    historyLockedAt: null,
    steps,
    consulenzeOnly: true,
  });

  return {
    sequenceGroupId,
    firstPercorsoId: ref.id,
    totalPercorsi: 1,
    attachedConsulenze: consNutr + consTech,
  };
}

/* -------- template steps con plannedAt --------
   Compatibile all’indietro:
   - buildStepsWithPlan(start)                             -> senza consulenze
   - buildStepsWithPlan(start, true)                       -> include UNA consulenza "senza tipo"
   - buildStepsWithPlan(start, { includeConsulenza: true })
   - buildStepsWithPlan(start, { consulenze: { tecnica, nutrizionale } })
*/
export function buildStepsWithPlan(plannedStartAtISO, opts = {}) {
  const isBool = typeof opts === 'boolean';
  const normalized = isBool ? { includeConsulenza: opts } : (opts || {});
  const includeConsulenza = !!normalized.includeConsulenza;
  const includeQuestionario = normalized.includeQuestionario !== false;

  const consFromCounts = normalized.consulenze && (
    _toInt(normalized.consulenze.tecnica) > 0 || _toInt(normalized.consulenze.nutrizionale) > 0
  );

  // questionario parte dall'inizio percorso
  const questionarioAt = plannedStartAtISO;

  // consegna: se il questionario è presente aspettiamo 3 giorni, altrimenti partiamo subito
  const consegnaAt = includeQuestionario ? addDaysISO(questionarioAt, 3) : questionarioAt;

  // feedback: 7 giorni dopo la consegna
  const feedbackAt = addDaysISO(consegnaAt, 7);

  // check finale: 30 giorni dopo la consegna
  const checkAt = addDaysISO(consegnaAt, 30);

  const steps = [];
  if (includeQuestionario) {
    steps.push({
      name: 'questionario',
      plannedAt: questionarioAt,
      done: false,
      doneAt: null,
      questionnaire: null,
      adminStatus: 'none',
      adminNote: null,
      staffNote: null,
    });
  }

  // CONSULENZE
  if (consFromCounts) {
    const t = _toInt(normalized.consulenze.tecnica);
    const n = _toInt(normalized.consulenze.nutrizionale);
    if (n > 0) {
      steps.push(..._buildConsulenze('Nutrizionale', n, questionarioAt, {
        offsetDays: 1,
        placement: 'before-consegna',
      }));
    }
    steps.push({ name: 'consegna programmi', plannedAt: consegnaAt, done: false, doneAt: null });
    steps.push(..._buildConsulenze('Tecnica', t, consegnaAt, {
      offsetDays: 1,
      placement: 'after-consegna',
    }));
  } else if (includeConsulenza) {
    // compatibilità: una sola consulenza “senza tipo” (post-consegna)
    steps.push({ name: 'consegna programmi', plannedAt: consegnaAt, done: false, doneAt: null });
    steps.push({
      name: 'consulenza',
      plannedAt: addDaysISO(consegnaAt, 1),
      done: false,
      doneAt: null,
      consulenzaStatus: 'Da fare',
      consulenzaPlacement: 'after-consegna',
      consulenzaOffsetDays: 1,
    });
  } else {
    steps.push({ name: 'consegna programmi', plannedAt: consegnaAt, done: false, doneAt: null });
  }

  steps.push(
    { name: 'feedback programmi',      plannedAt: feedbackAt,     done: false, doneAt: null, questionnaire: null },
    { name: 'scadenza / check finale', plannedAt: checkAt,        done: false, doneAt: null, questionnaire: null },
  );

  return steps;
}

/**
 * Ricalcola plannedAt degli step dipendenti, tenendo conto di eventuali doneAt.
 * NON tocca gli step già completati.
 * Ritorna un nuovo array steps.
 */
export function recalcPlannedSteps(percorso) {
  const steps = Array.isArray(percorso?.steps) ? [...percorso.steps] : [];
  if (!steps.length) return steps;

  const getIdx = (label) =>
    steps.findIndex(s => (s?.name || '').toLowerCase().includes(label));

  const idxQ = getIdx('questionario');
  const idxC = getIdx('consegna');
  const idxF = getIdx('feedback');
  const checkCandidates = steps
    .map((step, idx) => ({ step, idx }))
    .filter(({ step }) => stepKeyFromName(step?.name) === 'check_finale');
  const idxK = checkCandidates.find(({ step }) => !step?.convertedFromQuestionario)?.idx
    ?? checkCandidates[0]?.idx
    ?? -1;

  const baseStart = percorso?.plannedStartAt || percorso?.createdAt || new Date().toISOString();

  // (1) questionario: se non fatto, planned = baseStart
  if (idxQ >= 0) {
    if (!steps[idxQ].done) steps[idxQ].plannedAt = baseStart;
  }

  // ancora per la consegna
  const anchorQ = (idxQ >= 0 && (steps[idxQ].doneAt || steps[idxQ].plannedAt)) || baseStart;

  // (2) consegna: 3 giorni dopo l'ancora del questionario (o doneAt se presente)
  if (idxC >= 0 && !steps[idxC].done) {
    steps[idxC].plannedAt = addDaysISO(anchorQ, 3);
  }

  const anchorC = (idxC >= 0 && (steps[idxC].doneAt || steps[idxC].plannedAt)) || addDaysISO(anchorQ, 3);

  // (2.5) per TUTTE le consulenze non fatte: 1 giorno dopo la consegna
  for (let i = 0; i < steps.length; i++) {
    if (stepKeyFromName(steps[i]?.name) === 'consulenza' && !steps[i].done) {
      const placement = steps[i].consulenzaPlacement || 'after-consegna';
      const offset = Number.isFinite(Number(steps[i].consulenzaOffsetDays))
        ? Number(steps[i].consulenzaOffsetDays)
        : 1;
      const base = placement === 'before-consegna' ? anchorQ : anchorC;
      steps[i].plannedAt = addDaysISO(base, offset);
    }
  }

  // (3) feedback: 7 giorni dopo consegna
  if (idxF >= 0 && !steps[idxF].done) {
    steps[idxF].plannedAt = addDaysISO(anchorC, 7);
  }

  // (4) check finale: 30 giorni dopo consegna
  if (idxK >= 0) {
    const hasRealChecks = checkCandidates.some(({ step }) => !step?.convertedFromQuestionario);
    if (hasRealChecks) {
      checkCandidates
        .filter(({ step }) => !step?.convertedFromQuestionario && !step?.done)
        .forEach(({ idx }) => {
          steps[idx].plannedAt = addDaysISO(anchorC, 30);
        });
    } else if (!steps[idxK].done) {
      steps[idxK].plannedAt = addDaysISO(anchorC, 30);
    }
  }

  return steps;
}

/**
 * Sposta le plannedAt (e gli anchor principali) dei programmi successivi nella stessa sequenza,
 * solo per step non completati.
 */
/**
 * Riprogramma i percorsi successivi partendo da una consegna effettiva.
 * Regola: consegna → check finale a +30 giorni (calendario)
 *         check finale → consegna programma successivo a +3 giorni lavorativi
 */
async function rescheduleSequenceAfterConsegna({ sequenceGroupId, baseSequenceIndex, consegnaDoneAt }) {
  const anchor = toDateSafe(consegnaDoneAt);
  if (!sequenceGroupId || !anchor) return;

  const snap = await getDocs(query(collection(db, 'percorsi'), where('sequenceGroupId', '==', sequenceGroupId)));
  const docs = (snap.docs || []).slice().sort((a, b) => {
    const ia = Number(a.data()?.sequenceIndex) || 0;
    const ib = Number(b.data()?.sequenceIndex) || 0;
    return ia - ib;
  });

  let prevConsegnaIso = anchor.toISOString();

  for (const d of docs) {
    const data = d.data() || {};
    const idx = Number(data.sequenceIndex);
    if (!Number.isFinite(idx) || idx <= baseSequenceIndex) continue;
    if (data.consulenzeOnly) continue;
    if (data.historyLocked) continue;

    const checkAfterPrev = addDaysISO(prevConsegnaIso, 30);
    const nextConsegnaIso = addWorkingDaysISO(checkAfterPrev, 3);
    const plannedStartAt = addDaysISO(nextConsegnaIso, -3);

    let steps = Array.isArray(data.steps) ? [...data.steps] : [];
    const idxC = steps.findIndex((s) => stepKeyFromName(s?.name) === 'consegna');
    if (idxC >= 0 && !steps[idxC]?.done) {
      steps[idxC] = { ...steps[idxC], plannedAt: nextConsegnaIso };
    }

    const nextPercorso = { ...data, plannedStartAt, steps };
    steps = recalcPlannedSteps(nextPercorso);

    const payload = {
      steps,
      plannedStartAt,
      expireAt: resolveExpireAt({ ...data, plannedStartAt, steps }),
    };

    try {
      await updateDoc(doc(db, 'percorsi', d.id), payload);
    } catch (err) {
      console.warn('rescheduleSequenceAfterConsegna error', d.id, err);
    }

    prevConsegnaIso = nextConsegnaIso || prevConsegnaIso;
  }
}

/* --------- CREAZIONE PERCORSI (con plannedAt e expireAt) ---------- */
export async function createPercorsiForProduct({
  uid,
  userEmail = null,
  product,
  coachingQty,
  quantityMultiplier = 1,
  orderRef = null,
  orderSource = null,
  nowIso = new Date().toISOString(),
  plannedStartAt = null,
  titleBase,
  historyLockCount = 0,
  fixedMonthsPerProgram = null,
  orderSequencePriority = null,
  orderSequenceRole = null,
  orderSequenceAnchor = null,
}) {
  const productId   = product?.id || null;
  const productName = product?.name || 'Prodotto';
  const createdAtIso = toDateSafe(nowIso)?.toISOString() || new Date().toISOString();
  const baseStartIso = toDateSafe(plannedStartAt)?.toISOString() || createdAtIso;

  // quanti percorsi totali
  const baseQty = Number.isFinite(Number(coachingQty))
    ? Number(coachingQty)
    : Number(product?.coaching_quantity) || 0; // <-- consente 0

  const totalPercorsi = Math.max(0, baseQty) * Math.max(1, Number(quantityMultiplier) || 1);

  // quantità consulenze dal prodotto
  const consNutr = _toInt(product?.cons_nutrizionali);
  const consTech = _toInt(product?.cons_tecniche);
  const hasAnyCons = (consNutr + consTech) > 0;

  const durationMonthsRaw = Number(product?.duration_months);
  const monthsTotal = (Number.isFinite(durationMonthsRaw) && durationMonthsRaw > 0)
    ? durationMonthsRaw
    : 1;

  // ✅ Caso speciale: nessun percorso da creare ma ci sono consulenze → crea un percorso "solo consulenze"
  if (totalPercorsi === 0 && hasAnyCons) {
    return _createConsulenzeOnlyPercorso({
      uid,
      userEmail,
      productId,
      productName,
      orderRef,
      orderSource,
      nowIso: createdAtIso,
      plannedStartAt: baseStartIso,
      titleBase,
      durationMonths: monthsTotal,
      consTech,
      consNutr,
    });
  }

  // Se non ci sono percorsi e nemmeno consulenze, non faccio nulla
  if (totalPercorsi === 0) {
    return { sequenceGroupId: null, firstPercorsoId: null, totalPercorsi: 0, attachedConsulenze: 0 };
  }

  // quota mensile per singolo percorso (approssimiamo all'intero >=1)
  const monthsPer = Number.isFinite(Number(fixedMonthsPerProgram)) && Number(fixedMonthsPerProgram) > 0
    ? Number(fixedMonthsPerProgram)
    : Math.max(1, Math.round(monthsTotal / totalPercorsi));

  // distribuzione equa per-percorso (round-robin su eventuale resto)
  const baseTech = Math.floor(consTech / totalPercorsi);
  const remTech  = consTech % totalPercorsi;
  const baseNutr = Math.floor(consNutr / totalPercorsi);
  const remNutr  = consNutr % totalPercorsi;

  const sequenceGroupId = _generateSequenceGroupId();

  let firstPercorsoId = null;

  const historyCount = Math.max(0, Math.min(
    Number.isFinite(Number(historyLockCount)) ? Number(historyLockCount) : 0,
    totalPercorsi
  ));

  for (let i = 0; i < totalPercorsi; i++) {
    // start pianificato per questo percorso
    const plannedStart = new Date(baseStartIso);
    const monthOffset = (i - historyCount) * monthsPer;
    plannedStart.setMonth(plannedStart.getMonth() + monthOffset);
    const plannedStartAt = plannedStart.toISOString();

    // quante consulenze assegnare a QUESTO percorso
    const consForThis = {
      tecnica: baseTech + (i < remTech ? 1 : 0),
      nutrizionale: baseNutr + (i < remNutr ? 1 : 0),
    };

    // steps con consulenze tipizzate (se >0)
    const steps = buildStepsWithPlan(plannedStartAt, {
      consulenze: consForThis,
      includeQuestionario: i === 0,
    });
    const expireAt = resolveExpireAt({
      plannedStartAt,
      createdAt: createdAtIso,
      duration_months: monthsPer,
      steps,
    });

    const historyLocked = i < historyCount;

    const ref = await addDoc(collection(db, 'percorsi'), {
      uid: uid || null,
      userEmail,
      title: (titleBase || productName) + ` - Percorso ${i + 1}/${totalPercorsi}`,
      productId,
      productName,
      sequenceGroupId,
      sequenceIndex: i + 1,
      sequenceTotal: totalPercorsi,
      createdAt: createdAtIso,
      plannedStartAt,
      // metadati di durata/scadenza per il percorso
      duration_months: monthsPer,
      expireAt,
      orderRef: orderRef ? String(orderRef) : null,
      orderSource: orderSource || null,
      orderSequencePriority: Number.isFinite(Number(orderSequencePriority))
        ? Number(orderSequencePriority)
        : null,
      orderSequenceRole: orderSequenceRole || null,
      orderSequenceAnchor: toDateSafe(orderSequenceAnchor)?.toISOString() || null,
      postponeDays: 0,
      historyLocked,
      historyLockedAt: historyLocked ? nowIso : null,
      steps,
    });

    if (i === 0) firstPercorsoId = ref.id;
  }

  return { sequenceGroupId, firstPercorsoId, totalPercorsi, attachedConsulenze: 0 };
}

export async function createConsulenzeOnlyPercorso({
  uid,
  userEmail = null,
  productId = null,
  productName = 'Consulenze',
  titleBase = 'Consulenze',
  durationMonths = 1,
  consNutrizionali = 0,
  consTecniche = 0,
  nowIso = new Date().toISOString(),
  plannedStartAt = null,
}) {
  const consNutr = _toInt(consNutrizionali);
  const consTech = _toInt(consTecniche);
  if (!uid) throw new Error('Missing user id');
  if ((consNutr + consTech) <= 0) throw new Error('Nessuna consulenza da creare');

  const monthsTotal = Number.isFinite(Number(durationMonths)) && Number(durationMonths) > 0
    ? Number(durationMonths)
    : 1;

  return _createConsulenzeOnlyPercorso({
    uid,
    userEmail,
    productId,
    productName,
    orderRef: null,
    orderSource: 'manual',
    nowIso,
    plannedStartAt,
    titleBase,
    durationMonths: monthsTotal,
    consTech,
    consNutr,
  });
}

export function buildCheckFinalWorkedMessage(percorso) {
  const programTitle = percorso?.title || percorso?.productName || '';
  const displayTitle = programTitle ? ` "${programTitle}"` : '';
  return `Abbiamo completato il check di confronto del programma${displayTitle} puoi vederlo nella sezione 'I tuoi Check'.`;
}

/* --------- AZIONI DI AGGIORNAMENTO ---------- */

/**
 * Segna uno step come fatto/non fatto e ricalcola le plannedAt delle fasi dipendenti.
 * @param {string} percorsoId
 * @param {number} stepIndex
 * @param {boolean} wantDone
 */
export async function setStepDone(percorsoId, stepIndex, wantDone) {
  const snap = await getDoc(doc(db, 'percorsi', percorsoId));
  if (!snap.exists()) return;
  const data = snap.data() || {};
  const steps = Array.isArray(data.steps) ? [...data.steps] : [];
  if (!steps[stepIndex]) return;

  const currentStep = steps[stepIndex];
  const wasDone = !!currentStep?.done;
  const nextDone = !!wantDone;
  if (wasDone === nextDone) {
    return;
  }

  const key = stepKeyFromName(currentStep?.name);
  const userUid = data.uid || data.userUid || null;
  const doneAtIso = wantDone ? new Date().toISOString() : null;

  steps[stepIndex] = {
    ...steps[stepIndex],
    done: !!wantDone,
    doneAt: doneAtIso,
  };

  const autoScalata = key === 'check_finale' && nextDone
    ? applyAutomaticTechnicalConsulenzeScalata(steps, { nowIso: doneAtIso })
    : { steps, changed: false, checkEntry: null, changes: [] };

  // ricalcolo plannedAt per gli step NON completati
  const next = recalcPlannedSteps({ ...data, steps: autoScalata.steps });

  const consegnaDerivedFields = key === 'consegna'
    ? buildConsegnaDerivedFields({ ...data, steps: next })
    : {};

  await updateDoc(doc(db, 'percorsi', percorsoId), {
    steps: next,
    expireAt: resolveExpireAt({ ...data, steps: next }),
    ...consegnaDerivedFields,
  });

  if (key === 'consegna') {
    if (!userUid) {
      console.warn(`setStepDone: impossibile trovare uid per sincronizzare la consegna ${key}.`);
    } else {
      try {
        await syncConsegnaMirrorForInvii({
          userUid,
          percorsoId,
          done: nextDone,
          doneAtIso,
        });
      } catch (err) {
        console.warn('setStepDone: sync invii/alimentazioni consegna fallita', err);
      }
    }
  }

  if (wantDone) {
    if (key === 'consegna') {
      const sequenceGroupId = data.sequenceGroupId || percorsoId;
      const sourceSequenceIndex = Number(data.sequenceIndex) || 1;
      await rescheduleSequenceAfterConsegna({
        sequenceGroupId,
        baseSequenceIndex: sourceSequenceIndex,
        consegnaDoneAt: doneAtIso,
      });
    }

    if (key === 'consegna' && data.notificationsEnabled !== false) {
      if (!userUid) {
        console.warn(`setStepDone: impossibile trovare uid per inviare ticket ${key}.`);
        return;
      }
      try {
        const userSnap = await getDoc(doc(db, 'users', userUid));
        if (userSnap.exists() && shouldSuppressNotifications(userSnap.data() || {})) {
          return;
        }
      } catch (err) {
        console.warn('setStepDone: controllo sospensione operativa fallito', err);
      }
      const programTitle = data.title || data.productName || null;
      const sequenceIndex = Number(data.sequenceIndex) || null;
      const sequenceTotal = Number(data.sequenceTotal) || null;
      const shouldAddSenzaPensieri =
        sequenceIndex === 1 && [4, 6, 10].includes(sequenceTotal) && !isTopProduct(data);
      const { isSoloAllenamento, isSoloAlimentazione } = resolveProgramDeliveryMode(data);
      const conflict = isSoloAllenamento && isSoloAlimentazione;
      const includeAllenamento = conflict || !isSoloAlimentazione;
      const includeAlimentazione = conflict || !isSoloAllenamento;
      const supportLine = conflict || (!isSoloAllenamento && !isSoloAlimentazione)
        ? 'Per assistenza nutrizionale/tecnica, alimentazione e ricette resta sempre sull’app Fusco Fit Connect (logo nero).'
        : (isSoloAllenamento
          ? 'Per assistenza tecnica resta sempre sull’app Fusco Fit Connect (logo nero).'
          : 'Per assistenza nutrizionale, alimentazione e ricette resta sempre sull’app Fusco Fit Connect (logo nero).');
      const messageLines = [
        programTitle
          ? `Ciao! abbiamo consegnato il tuo programma "${programTitle}" 🏋️‍♂️💪`
          : 'Ciao! abbiamo consegnato il tuo programma 🏋️‍♂️💪',
        ...(includeAllenamento
          ? ['ALLENAMENTO caricato: lo trovi nell’app Fusco Fit Connect (logo nero), nella sezione "Allenamenti".']
          : []),
        ...(includeAlimentazione
          ? ['ALIMENTAZIONE caricata: la trovi nell’app Fusco Fit Connect (logo nero) nella sezione "Alimentazione".']
          : []),
        ...(includeAllenamento
          ? [
              'Scegli i carichi puntando alla massima intensità con tecnica corretta. Leggi le note e le indicazioni presenti in ogni esercizio.',
            ]
          : []),
        supportLine,
        'Aspettiamo un tuo feedback dei primi giorni: non tenerti dubbi, partiamo bene insieme! 🥰💪🏻',
        'Supporto e guida completa: https://www.metodofusco.it/tickets/#/come-funziona'
      ];
      if (shouldAddSenzaPensieri) {
        messageLines.push(
          [
            'Entro 10 giorni dall\'acquisto puoi rendere il tuo pacchetto più flessibile e sereno aggiungendo il servizio "Senza Pensieri" (se non lo hai già fatto).',
            'Cosa include:',
            '• Problemi di salute (con certificato): rimborso tramite nota di credito oppure sospensione del piano',
            '• Problemi personali certificabili: rimborso (nota di credito) o sospensione garantiti',
            '• Problemi personali non certificabili: rimborso (nota di credito) o sospensione previa approvazione',
            'Durata: valida per tutto il pacchetto attivo appena acquistato',
            'Costo: 69 € per FC 4 e FC 6',
            'Importante: i rimborsi avvengono solo tramite nota di credito, utilizzabile entro 24 mesi per altri servizi Fusco.',
            'Contattaci per attivarlo: dopo 10 giorni dalla consegna del programma non sarà più possibile.'
          ].join('\n')
        );
      }
      const message = messageLines.join('\n\n');
      const ticketExtra = {
        tipoTicket: 'Tecnico',
        percorsoId,
        stepKey: key,
      };
      if (programTitle) ticketExtra.programTitle = programTitle;
      if (data.orderRef) ticketExtra.orderRef = String(data.orderRef);
      if (data.orderSource) ticketExtra.orderSource = data.orderSource;
      if (data.orderSource === 'manuale' || data.manualOrder) ticketExtra.manualOrder = true;
      if (data.sequenceGroupId) ticketExtra.sequenceGroupId = data.sequenceGroupId;
      if (Number.isFinite(Number(data.sequenceIndex))) ticketExtra.sequenceIndex = Number(data.sequenceIndex);
      try {
        await appendTicketMessage({
          db,
          userUid,
          message,
          title: 'Aggiornamento programma',
          ticketExtra,
          preserveOpenStatus: true,
          notifyUser: true,
        });
      } catch (err) {
        console.warn('setStepDone: appendTicketMessage fallita', err);
      }
    }
  }
}

/**
 * Aggiorna il flag "Prog. customizzato" sullo step consegna rispettando la sequenza:
 * se un percorso #N è customizzato, anche i precedenti <=N devono risultare customizzati.
 * I percorsi successivi (>N) restano invariati a false finché non vengono lavorati.
 * @param {string} percorsoId
 * @param {boolean} wantCustom
 * @returns {Promise<{userUid: string|null, stateByPercorsoId: Record<string, {progCustomizzato: boolean, progCustomizzatoAt: string|null}>}>}
 */
export async function setProgCustomFlag(percorsoId, wantCustom) {
  if (!percorsoId) return { userUid: null, stateByPercorsoId: {} };

  const targetRef = doc(db, 'percorsi', percorsoId);
  const targetSnap = await getDoc(targetRef);
  if (!targetSnap.exists()) {
    return { userUid: null, stateByPercorsoId: {} };
  }

  const targetData = targetSnap.data() || {};
  const userUid = targetData.uid || targetData.userUid || null;
  const targetSequenceGroupId = targetData.sequenceGroupId || null;
  const desiredCustom = !!wantCustom;
  const opNowIso = new Date().toISOString();

  let sequenceDocs = [{ id: targetSnap.id, data: targetData }];
  if (userUid) {
    const userPercorsiSnap = await getDocs(
      query(collection(db, 'percorsi'), where('uid', '==', userUid))
    );
    if (!userPercorsiSnap.empty) {
      sequenceDocs = userPercorsiSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        data: docSnap.data() || {},
      }));
    }
  }

  const normalizedSequence = sequenceDocs
    .map((item) => {
      const steps = Array.isArray(item.data?.steps) ? [...item.data.steps] : [];
      const consegnaIndex = getConsegnaStepIndex(steps);
      if (consegnaIndex < 0) return null;
      const consegnaStep = steps[consegnaIndex] || {};
      const currentState = resolveProgCustomState(consegnaStep);
      return {
        id: item.id,
        ref: doc(db, 'percorsi', item.id),
        steps,
        consegnaIndex,
        sequenceGroupId: item.data?.sequenceGroupId || null,
        sequenceIndex: toPositiveInt(item.data?.sequenceIndex),
        currentCustom: currentState.progCustomizzato,
        currentCustomAt: currentState.progCustomizzatoAt,
      };
    })
    .filter(Boolean);

  const relevantItems = normalizedSequence.filter((item) => {
    if (targetSequenceGroupId) {
      return item.sequenceGroupId === targetSequenceGroupId;
    }
    return item.id === percorsoId;
  });

  if (!relevantItems.length) {
    return { userUid, stateByPercorsoId: {} };
  }

  const targetItem = relevantItems.find((item) => item.id === percorsoId);
  if (!targetItem) {
    return { userUid, stateByPercorsoId: {} };
  }

  let maxTrueSequenceIndex = null;
  relevantItems.forEach((item) => {
    const isTarget = item.id === percorsoId;
    const baseCustom = isTarget ? desiredCustom : item.currentCustom;
    if (!baseCustom) return;
    if (!item.sequenceIndex) return;
    if (!maxTrueSequenceIndex || item.sequenceIndex > maxTrueSequenceIndex) {
      maxTrueSequenceIndex = item.sequenceIndex;
    }
  });

  const writes = [];
  const updatesByPercorsoId = new Map();

  relevantItems.forEach((item) => {
    const isTarget = item.id === percorsoId;
    const hasSequenceIndex = !!item.sequenceIndex;
    let nextCustom = isTarget ? desiredCustom : item.currentCustom;

    if (hasSequenceIndex && maxTrueSequenceIndex) {
      nextCustom = item.sequenceIndex <= maxTrueSequenceIndex;
    }

    const nextCustomAt = nextCustom ? (item.currentCustomAt || opNowIso) : null;

    updatesByPercorsoId.set(item.id, {
      progCustomizzato: nextCustom,
      progCustomizzatoAt: nextCustomAt,
    });

    if (item.currentCustom === nextCustom && item.currentCustomAt === nextCustomAt) {
      return;
    }

    const nextSteps = [...item.steps];
    const previousStep = nextSteps[item.consegnaIndex] || {};
    nextSteps[item.consegnaIndex] = {
      ...previousStep,
      progCustomizzato: nextCustom,
      progCustomizzatoAt: nextCustomAt,
    };

    writes.push(updateDoc(item.ref, { steps: nextSteps }));
  });

  if (writes.length) {
    await Promise.all(writes);
  }

  if (userUid && updatesByPercorsoId.size) {
    try {
      await syncProgCustomMirrorForInvii({
        userUid,
        updatesByPercorsoId,
      });
    } catch (err) {
      console.warn('setProgCustomFlag: sync invii/alimentazioni prog custom fallita', err);
    }
  }

  const stateByPercorsoId = {};
  updatesByPercorsoId.forEach((state, id) => {
    stateByPercorsoId[id] = {
      progCustomizzato: !!state.progCustomizzato,
      progCustomizzatoAt: state.progCustomizzato
        ? (normalizeIso(state.progCustomizzatoAt) || opNowIso)
        : null,
    };
  });

  return {
    userUid,
    stateByPercorsoId,
  };
}

/**
 * Aggiorna manualmente la data di completamento della consegna e riallinea le planned future.
 * Usa la differenza rispetto alla precedente doneAt (o plannedAt se mancava) per propagare il delta.
 */
export async function updateConsegnaDoneAt(percorsoId, stepIndex, dateIso) {
  const ref = doc(db, 'percorsi', percorsoId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data() || {};
  const steps = Array.isArray(data.steps) ? [...data.steps] : [];
  const step = steps[stepIndex];
  if (!step) return;
  if (stepKeyFromName(step?.name) !== 'consegna') return;

  const nextDoneAt = toDateSafe(dateIso);
  if (!nextDoneAt) return;

  steps[stepIndex] = {
    ...step,
    done: true,
    doneAt: nextDoneAt.toISOString(),
  };

  const nextSteps = recalcPlannedSteps({ ...data, steps });
  await updateDoc(ref, {
    steps: nextSteps,
    expireAt: resolveExpireAt({ ...data, steps: nextSteps }),
    ...buildConsegnaDerivedFields({ ...data, steps: nextSteps }),
  });

  const userUid = data.uid || data.userUid || null;
  if (userUid) {
    try {
      await syncConsegnaMirrorForInvii({
        userUid,
        percorsoId,
        done: true,
        doneAtIso: nextDoneAt.toISOString(),
      });
    } catch (err) {
      console.warn('updateConsegnaDoneAt: sync invii/alimentazioni consegna fallita', err);
    }
  }

  const sequenceGroupId = data.sequenceGroupId || percorsoId;
  const sourceSequenceIndex = Number(data.sequenceIndex) || 1;
  await rescheduleSequenceAfterConsegna({
    sequenceGroupId,
    baseSequenceIndex: sourceSequenceIndex,
    consegnaDoneAt: nextDoneAt.toISOString(),
  });
}

/**
 * Posticipa di N giorni tutti gli step NON completati (somma sul plannedAt).
 * Aggiorna anche postponeDays cumulativo.
 * Aggiorna anche expireAt (se presente o calcolabile) per tenere allineata la scadenza.
 * @param {string} percorsoId
 * @param {number} days
 * @param {{ shiftExpire?: boolean }} options
 */
export async function postponePercorso(percorsoId, days, options = {}) {
  const add = Number(days) || 0;
  if (!add) return;
  const shiftExpire = options.shiftExpire !== false;
  const ref = doc(db, 'percorsi', percorsoId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data() || {};
  const steps = Array.isArray(data.steps) ? [...data.steps] : [];
  const shifted = steps.map(s => {
    if (s.done) return s; // non tocco gli step completati
    return { ...s, plannedAt: addDaysISO(s.plannedAt, add) };
  });
  const totalPostpone = (Number(data.postponeDays) || 0) + add;
  const baseExpire = resolveExpireAt(data);
  const nextExpire = shiftExpire
    ? (
      data?.consulenzeOnly
        ? addDaysISO(baseExpire, add)
        : resolveExpireAt({ ...data, steps: shifted })
    )
    : baseExpire;
  await updateDoc(ref, { steps: shifted, postponeDays: totalPostpone, expireAt: nextExpire || null });
}

/**
 * Azzera il posticipo: riportiamo le plannedAt al valore “teorico” coerente
 * con plannedStartAt e con eventuali doneAt reali.
 * Resetta anche expireAt al valore base (se ricavabile).
 */
export async function resetPostpone(percorsoId) {
  const ref = doc(db, 'percorsi', percorsoId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data() || {};

  const current = Array.isArray(data.steps) ? data.steps : [];

  // ricostruisco il numero e il tipo di consulenze presenti ora
  const present = _countConsulenzeByType(current);
  const hadQuestionario = current.some(step => stepKeyFromName(step?.name) === 'questionario');

  // 1) baseline coerente con la presenza/numero di consulenze
  let steps = buildStepsWithPlan(
    data.plannedStartAt || data.createdAt || new Date().toISOString(),
    {
      consulenze: { tecnica: present.tecnica, nutrizionale: present.nutrizionale },
      includeQuestionario: hadQuestionario,
    }
  );

  // 2) re-inietto done/doneAt e metadati esistenti, step-by-step
  steps = steps.map((s, i) => {
    const prev = current[i] || {};
    const key = stepKeyFromName(s.name);
    const base = {
      ...s,
      done: !!prev.done,
      doneAt: prev.doneAt || null,
      questionnaire: prev.questionnaire || s.questionnaire || null,
      adminStatus: prev.adminStatus || (key === 'questionario' ? (prev.adminStatus || 'none') : prev.adminStatus) || 'none',
      adminNote: prev.adminNote ?? null,
      staffNote: prev.staffNote ?? null,
    };

    // Keep the reminder attached to the same source step through schedule resets.
    if (stepKeyFromName(prev.name) === key && prev.consultationReminderStepId) {
      base.consultationReminderStepId = prev.consultationReminderStepId;
    }

    if (key === 'consulenza') {
      base.consulenzaStatus = prev.consulenzaStatus || 'Da fare';
      base.consulenzaType   = prev.consulenzaType   || s.consulenzaType || 'Tecnica';
      base.TrainerNome      = prev.TrainerNome || '';
      base.scalataAt        = prev.scalataAt || null;
    }
    return base;
  });

  // 3) ricalcolo dipendenze considerando i doneAt reali
  steps = recalcPlannedSteps({ ...data, steps });

  const restoredExpireAt = resolveExpireAt({ ...data, steps });

  await updateDoc(ref, { steps, postponeDays: 0, expireAt: restoredExpireAt });
}
